import React from "react";
import { Button, Chip, Link as MuiLink, Typography } from "@mui/material";
import { DataCardShell } from "../../../../../components/responsive";
import type { LifecycleRetreat } from "../../../../../types/retreatLifecycle";
import {
  formatRetreatDateRange,
  isUpcomingRetreat,
} from "../../lifecycle/lifecycleUi";
import { projectedSupportParts, retreatDisplayTitle, retreatLocationLine } from "../manageRetreatTypes";
import { RegistrationOpenActions } from "./RegistrationOpenActions";
import { dashboardOutlinedButtonSx } from "../dashboardUi";

export function RegistrationOpenCard({
  r,
  isSuperAdmin,
  canEdit,
  selected,
  onSelectChange,
  onHistory,
  onDelete,
  onStatusToggle,
  onEditInactiveReason,
  onWaitlistExport,
  onFreezeToggle,
}: {
  r: LifecycleRetreat;
  isSuperAdmin: boolean;
  canEdit: boolean;
  selected?: boolean;
  onSelectChange?: (checked: boolean) => void;
  onHistory: (r: LifecycleRetreat) => void;
  onDelete: (r: LifecycleRetreat) => void;
  onStatusToggle: (r: LifecycleRetreat) => void;
  onEditInactiveReason: (r: LifecycleRetreat) => void;
  onWaitlistExport: (r: LifecycleRetreat) => void;
  onFreezeToggle: (r: LifecycleRetreat) => void;
}) {
  const { typeLabel, couple } = projectedSupportParts(r);
  const support =
    [typeLabel, couple].filter(Boolean).join(" · ") || "Missing";

  return (
    <DataCardShell
      selectable={true}
      selected={selected}
      onSelectChange={onSelectChange}
      highlight={isUpcomingRetreat(r)}
      title={
        <MuiLink
          href={`/retreatRoster?retreatId=${r.id}`}
          onClick={(e) => {
            e.preventDefault();
            window.open(`/retreatRoster?retreatId=${r.id}`, "_blank");
          }}
          sx={{ color: "#1265c4", fontWeight: 800, textDecoration: "underline" }}
        >
          {retreatDisplayTitle(r)}
        </MuiLink>
      }
      subtitle={retreatLocationLine(r) || undefined}
      chips={
        <Chip
          size="small"
          label={r.status === "ACTIVE" ? "Open" : r.status || "Open"}
          sx={{ bgcolor: "#dcecff", color: "#0d5ea6", fontWeight: 700 }}
        />
      }
      fields={[
        {
          label: "Dates",
          value: formatRetreatDateRange(r.dateFrom, r.dateTo),
        },
        {
          label: "Lead Couple",
          value: r.leadCoupleName || "Not assigned",
        },
        { label: "Support", value: support },
        {
          label: "Retreatant Couples",
          value: (
            <MuiLink
              href={`/retreatRoster?retreatId=${r.id}&flag=1`}
              onClick={(e) => {
                e.preventDefault();
                window.open(`/retreatRoster?retreatId=${r.id}&flag=1`, "_blank");
              }}
            >
              {r.countRetreatant ?? 0}
            </MuiLink>
          ),
        },
        {
          label: "Team Couples",
          value: (
            <MuiLink
              href={`/retreatRoster?retreatId=${r.id}&flag=2`}
              onClick={(e) => {
                e.preventDefault();
                window.open(`/retreatRoster?retreatId=${r.id}&flag=2`, "_blank");
              }}
            >
              {r.countTeam ?? 0}
            </MuiLink>
          ),
        },
      ]}
      primaryAction={
        <Button
          size="small"
          variant="outlined"
          href={`/registration-retreat/${r.id}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ width: { xs: "100%", sm: "auto" }, ...dashboardOutlinedButtonSx }}
        >
          Public Page
        </Button>
      }
      secondaryActions={
        <RegistrationOpenActions
          row={r}
          isSuperAdmin={isSuperAdmin}
          canEdit={canEdit}
          onHistory={onHistory}
          onDelete={onDelete}
          onStatusToggle={onStatusToggle}
          onEditInactiveReason={onEditInactiveReason}
          onWaitlistExport={onWaitlistExport}
          onFreezeToggle={onFreezeToggle}
        />
      }
      footer={
        r.freeze === "FREEZE" ? (
          <Typography variant="caption" color="warning.main" fontWeight={700}>
            Frozen
          </Typography>
        ) : null
      }
    />
  );
}
